import React, { useState } from 'react';
import { 
  Send, 
  Edit, 
  Schedule, 
  CheckCircle, 
  Mail,
  PlayArrow 
} from '@mui/icons-material';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Alert, 
  TextField, 
  Stepper, 
  Step, 
  StepLabel, 
  CircularProgress 
} from '@mui/material';
import { useEmailSender } from '../hooks/useEmailSender';
import { generateEmailContent } from '../utils/emailTemplates';
import { useArtistMetrics } from '../hooks/useArtistMetrics';

const EmailSequence = ({ artistData, contactId }) => {
  const [sequenceStatus, setSequenceStatus] = useState('draft');
  const [artistEmailContent, setArtistEmailContent] = useState('');
  const [amandaEmailContent, setAmandaEmailContent] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('producer_outreach');
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingAmanda, setIsEditingAmanda] = useState(false);
  
  const { sendEmail, sending, error } = useEmailSender();
  const { metrics: artistMetrics } = useArtistMetrics(artistData?.artist_id);
  React.useEffect(() => {
    console.log('🔍 TESTING SUPABASE IMPORT...');
    import('../providers/supabase/supabase').then(module => {
      console.log('✅ SUPABASE IMPORTED:', !!module.supabase);
    }).catch(error => {
      console.error('❌ SUPABASE IMPORT FAILED:', error);
    });
  }, []);

  // Regenerate email content when artistData changes
  React.useEffect(() => {
    if (sequenceStatus === 'draft' && artistData && contactId) {
      console.log('🔄 ARTIST DATA CHANGED, regenerating email content...', {
        hasSelectedRecommendations: !!artistData.selectedRecommendations
      });
      
      // Fetch fresh selections for the preview too
      const fetchSelectionsForPreview = async () => {
        try {
          const { supabase } = await import('../providers/supabase/supabase');
          
          const { data: selections, error: selectionsError } = await supabase
            .from('selected_recommendations')
            .select('*')
            .eq('artist_id', artistData.artist_id || '9999999')
            .eq('contact_id', contactId)
            .single();

          if (!selectionsError && selections) {
            const { data: sonicRecs, error: sonicError } = await supabase
              .from('sonic_recommendations')
              .select('*')
              .eq('artist_id', artistData.artist_id || '9999999')
              .single();

            if (!sonicError && sonicRecs) {
              const allSimilarTracks = [
                { trackName: sonicRecs.similar_track_1_name, artistName: sonicRecs.similar_track_1_artist_name },
                { trackName: sonicRecs.similar_track_2_name, artistName: sonicRecs.similar_track_2_artist_name },
                { trackName: sonicRecs.similar_track_3_name, artistName: sonicRecs.similar_track_3_artist_name },
              ].filter(track => track.trackName && track.artistName);

              const selectedSimilarTracks = selections.selected_similar_tracks
                .map(index => allSimilarTracks[index])
                .filter(track => track);

              const freshArtistData = {
                ...artistData,
                selectedRecommendations: { similarTracks: selectedSimilarTracks }
              };
              
              console.log('✅ PREVIEW WITH FRESH SELECTIONS:', freshArtistData.selectedRecommendations);
              const artistEmail = generateEmailContent('artist_outreach', freshArtistData);
              setArtistEmailContent(artistEmail.content);
              return;
            }
          }
        } catch (err) {
          console.log('⚠️ Could not load selections for preview:', err);
        }
        
        // Fallback to normal generation
        const artistEmail = generateEmailContent('artist_outreach', artistData);
        setArtistEmailContent(artistEmail.content);
      };
      
      fetchSelectionsForPreview();
    }
  }, [artistData, sequenceStatus, contactId]);

  const templates = {
    producer_outreach: 'Producer Outreach - Standard',
    indie_pop: 'Indie Pop Rising',
    electronic: 'Electronic Breakout',
    singer_songwriter: 'Singer-Songwriter Discovery'
  };

  const steps = ['Amanda Analysis', 'Alexander Reply', 'Artist Outreach', 'Follow-ups'];
  
  const getActiveStep = () => {
    switch(sequenceStatus) {
      case 'draft': return -1;
      case 'amanda_ready': return 0;
      case 'amanda_sent': return 1;
      case 'ready_for_review': return 2;
      case 'artist_sent': return 3;
      case 'follow_ups': return 3;
      default: return -1;
    }
  };

  const startSequence = async () => {
    if (!artistData) return;
    
    // Merge regression data with metrics for complete artist profile
    const enhancedArtistData = {
      ...artistData,
      // Add Instagram followers from metrics if available
      instagram_followers: artistMetrics?.instagram_followers || null
    };
    
    console.log('🔍 ENHANCED ARTIST DATA:', {
      original_instagram: artistData.instagram_followers,
      metrics_instagram: artistMetrics?.instagram_followers,
      final_instagram: enhancedArtistData.instagram_followers
    });
    
    // Generate Amanda email for review with enhanced data
    const amandaEmail = generateEmailContent('amanda', enhancedArtistData);
    setAmandaEmailContent(amandaEmail.content);
    setSequenceStatus('amanda_ready');
  };

  const sendAmandaEmail = async () => {
    try {
      const timestamp = Date.now();
      const artistId = artistData.artist_id || 'unknown';
      const amandaMessageId = `amanda-${artistId}-${timestamp}`;
      
      // Send Amanda email with edited content
      await sendEmail({
        subject: `Quick Flag — ${artistData.name}`,
        from: 'goldfarb@freshlybakedstudios.com',
        to: 'almgren@freshlybakedstudios.com',
        content: amandaEmailContent,
        threadId: amandaMessageId
      });

      // Wait 2 seconds then send Alexander reply
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const alexanderEmail = generateEmailContent('alexander_reply', artistData);
      const alexanderMessageId = `alexander-${artistId}-${timestamp}`;
      await sendEmail({
        ...alexanderEmail,
        threadId: alexanderMessageId,
        inReplyTo: amandaMessageId,
        references: [amandaMessageId]
      });
      
      // Generate artist email for review WITH FRESH SELECTIONS
      console.log('🔄 FETCHING SELECTIONS FOR EMAIL PREVIEW...');
      let freshArtistData = artistData;
      
      try {
        const { supabase } = await import('../providers/supabase/supabase');
        
        const { data: selections, error: selectionsError } = await supabase
          .from('selected_recommendations')
          .select('*')
          .eq('artist_id', artistData.artist_id || '9999999')
          .eq('contact_id', contactId)
          .single();

        if (!selectionsError && selections) {
          const { data: sonicRecs, error: sonicError } = await supabase
            .from('sonic_recommendations')
            .select('*')
            .eq('artist_id', artistData.artist_id || '9999999')
            .single();

          if (!sonicError && sonicRecs) {
            const allSimilarTracks = [
              { trackName: sonicRecs.similar_track_1_name, artistName: sonicRecs.similar_track_1_artist_name },
              { trackName: sonicRecs.similar_track_2_name, artistName: sonicRecs.similar_track_2_artist_name },
              { trackName: sonicRecs.similar_track_3_name, artistName: sonicRecs.similar_track_3_artist_name },
            ].filter(track => track.trackName && track.artistName);

            const selectedSimilarTracks = selections.selected_similar_tracks
              .map(index => allSimilarTracks[index])
              .filter(track => track);

            freshArtistData = {
              ...artistData,
              selectedRecommendations: { similarTracks: selectedSimilarTracks }
            };
            
            console.log('✅ LOADED SELECTIONS FOR PREVIEW:', freshArtistData.selectedRecommendations);
          }
        }
      } catch (err) {
        console.log('⚠️ Could not load selections for preview:', err);
      }

      const artistEmail = generateEmailContent('artist_outreach', freshArtistData);
      setArtistEmailContent(artistEmail.content);
      
      // Store the threading info for when the artist email is sent
      const artistMessageId = `artist-${artistId}-${timestamp}`;
      window.emailThreadInfo = {
        threadId: artistMessageId,
        inReplyTo: alexanderMessageId,
        references: [amandaMessageId, alexanderMessageId]
      };
      
      setSequenceStatus('ready_for_review');
      
    } catch (err) {
      console.error('Error sending Amanda email:', err);
      setSequenceStatus('amanda_ready');
    }
  };

  const sendArtistEmail = async () => {
    // Fetch fresh selections data right before sending
    console.log('🔄 FETCHING FRESH SELECTIONS for email send...', {
      artistData_keys: Object.keys(artistData),
      artist_id_field: artistData.artist_id,
      id_field: artistData.id,
      contact_id_field: artistData.contact_id
    });
    let freshArtistData = artistData;
    
    try {
      const { supabase } = await import('../providers/supabase/supabase');
      
      // Get current selections for this artist/contact  
      const { data: selections, error: selectionsError } = await supabase
        .from('selected_recommendations')
        .select('*')
        .eq('artist_id', artistData.artist_id || artistData.id)
        .eq('contact_id', contactId)
        .single();

      if (!selectionsError && selections) {
        // Get sonic recommendations to build the selected tracks
        const { data: sonicRecs, error: sonicError } = await supabase
          .from('sonic_recommendations')
          .select('*')
          .eq('artist_id', artistData.artist_id || artistData.id)
          .single();

        if (!sonicError && sonicRecs) {
          const allSimilarTracks = [
            {
              trackName: sonicRecs.similar_track_1_name,
              artistName: sonicRecs.similar_track_1_artist_name,
            },
            {
              trackName: sonicRecs.similar_track_2_name,
              artistName: sonicRecs.similar_track_2_artist_name,
            },
            {
              trackName: sonicRecs.similar_track_3_name,
              artistName: sonicRecs.similar_track_3_artist_name,
            },
          ].filter(track => track.trackName && track.artistName);

          const selectedSimilarTracks = selections.selected_similar_tracks
            .map(index => allSimilarTracks[index])
            .filter(track => track);

          freshArtistData = {
            ...artistData,
            selectedRecommendations: {
              similarTracks: selectedSimilarTracks
            }
          };
          
          console.log('✅ FRESH SELECTIONS LOADED:', freshArtistData.selectedRecommendations);
        }
      }
    } catch (err) {
      console.log('⚠️ Could not load fresh selections:', err);
    }

    const artistEmail = generateEmailContent('artist_outreach', freshArtistData);
    const emailToSend = {
      ...artistEmail,
      content: artistEmailContent, // Use edited content
      // Add threading information stored from the sequence
      ...(window.emailThreadInfo || {})
    };
    
    const result = await sendEmail(emailToSend);
    if (result.success) {
      console.log('🎯 UPDATING DATABASE FOR ARTIST:', artistData.artist_id);
      try {
        const { supabase } = await import('../providers/supabase/supabase');
        const updateResult = await supabase
          .from('regression_prospects')
          .update({ 
            artist_email_sent_at: new Date().toISOString(),
            contacted_status: 'contacted'
          })
          .eq('artist_id', artistData.artist_id);
        
        console.log('📝 DATABASE UPDATE RESULT:', updateResult);
        
        // Call N8N via server endpoint (no CORS issues)
        fetch('/api/webhook/trigger-n8n', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            artist_id: artistData.artist_id,
            name: artistData.name,
            email: artistData.email,
            top_track: artistData.tracks_data?.top_track || 'their latest track'
          })
        }).then(r => r.json()).then(data => {
          if (data.success) {
            console.log('✅ N8N FOLLOW-UP SEQUENCE TRIGGERED');
          } else {
            console.log('❌ N8N WEBHOOK FAILED:', data.error);
          }
        }).catch(e => console.log('❌ N8N ERROR:', e));
        
      } catch (error) {
        console.error('❌ DATABASE UPDATE FAILED:', error);
      }
      setSequenceStatus('artist_sent');
      setTimeout(() => setSequenceStatus('follow_ups'), 1000);
    }
  };

  const resetSequence = () => {
    setSequenceStatus('draft');
    setArtistEmailContent('');
    setIsEditing(false);
    // Clear threading info
    window.emailThreadInfo = null;
  };

  if (!artistData) {
    return (
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography color="text.secondary">Loading artist data...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={3}>
          <Mail sx={{ mr: 1 }} />
          <Typography variant="h6">Email Outreach Sequence</Typography>
        </Box>

        {/* Template Selection */}
        {sequenceStatus === 'draft' && (
          <Box mb={3}>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>Select Template</InputLabel>
              <Select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                label="Select Template"
              >
                {Object.entries(templates).map(([key, name]) => (
                  <MenuItem key={key} value={key}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Sequence Progress Stepper */}
        <Box mb={3}>
          <Stepper activeStep={getActiveStep()} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Start Button */}
        {sequenceStatus === 'draft' && (
          <Box mb={3}>
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={20} /> : <PlayArrow />}
              onClick={startSequence}
              disabled={sending}
              size="large"
            >
              {sending ? 'Starting...' : 'Start Email Sequence'}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Will prepare Amanda analysis for your review and editing
            </Typography>
          </Box>
        )}

        {/* Amanda Email Review */}
        {sequenceStatus === 'amanda_ready' && (
          <Box mb={3}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center">
                <Mail sx={{ mr: 1 }} />
                Amanda Analysis Ready for Review
              </Box>
            </Alert>

            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="subtitle1">
                      <strong>From:</strong> Amanda Goldfarb (goldfarb@freshlybakedstudios.com)
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>To:</strong> Alexander Almgren (almgren@freshlybakedstudios.com)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Subject:</strong> Quick Flag — {artistData.name}
                    </Typography>
                  </Box>
                  <Button
                    startIcon={<Edit />}
                    onClick={() => setIsEditingAmanda(!isEditingAmanda)}
                    variant="outlined"
                    size="small"
                  >
                    {isEditingAmanda ? 'Preview' : 'Edit'}
                  </Button>
                </Box>
                
                {isEditingAmanda ? (
                  <TextField
                    multiline
                    rows={15}
                    fullWidth
                    value={amandaEmailContent}
                    onChange={(e) => setAmandaEmailContent(e.target.value)}
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                ) : (
                  <Box
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'inherit',
                      fontSize: '0.875rem',
                      color: 'text.primary',
                      margin: 0,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      backgroundColor: 'grey.50'
                    }}
                  >
                    {amandaEmailContent}
                  </Box>
                )}
              </CardContent>
            </Card>

            <Box display="flex" gap={2} mt={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={sending ? <CircularProgress size={20} /> : <Send />}
                onClick={sendAmandaEmail}
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Send to Alex'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setSequenceStatus('draft')}
              >
                Back
              </Button>
            </Box>
          </Box>
        )}

        {/* Internal Progress */}
        {sequenceStatus === 'internal_sent' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center">
              <CircularProgress size={20} sx={{ mr: 2 }} />
              Sending internal sequence...
            </Box>
          </Alert>
        )}

        {/* Artist Email Review */}
        {sequenceStatus === 'ready_for_review' && (
          <Box mb={3}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center">
                <Mail sx={{ mr: 1 }} />
                Artist Email Ready for Review
              </Box>
            </Alert>

            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="subtitle1">
                      <strong>To:</strong> {artistData.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Subject:</strong> "{artistData.tracks_data?.top_track}" — data & opportunity
                    </Typography>
                  </Box>
                  <Button
                    startIcon={<Edit />}
                    onClick={() => setIsEditing(!isEditing)}
                    variant="outlined"
                    size="small"
                  >
                    {isEditing ? 'Preview' : 'Edit'}
                  </Button>
                </Box>
                
                {isEditing ? (
                  <TextField
                    multiline
                    rows={20}
                    fullWidth
                    value={artistEmailContent}
                    onChange={(e) => setArtistEmailContent(e.target.value)}
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                ) : (
                  <Box
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'inherit',
                      fontSize: '0.875rem',
                      color: 'text.primary',
                      margin: 0,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      backgroundColor: 'grey.50'
                    }}
                  >
                    {artistEmailContent}
                  </Box>
                )}
              </CardContent>
            </Card>

            <Box display="flex" gap={2} mt={2}>
              <Button
                variant="contained"
                color="success"
                startIcon={sending ? <CircularProgress size={20} /> : <Send />}
                onClick={sendArtistEmail}
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Send to Artist'}
              </Button>
              <Button
                variant="outlined"
                onClick={resetSequence}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {/* Success States */}
        {sequenceStatus === 'artist_sent' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center">
              <CheckCircle sx={{ mr: 1 }} />
              Artist email sent successfully!
            </Box>
          </Alert>
        )}

{console.log('🔍 SEQUENCE STATUS:', sequenceStatus)}
        {sequenceStatus === 'follow_ups' && (
          <Box mb={3}>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center">
                <CheckCircle sx={{ mr: 1 }} />
                Sequence Active - Follow-ups Scheduled
              </Box>
            </Alert>
            
            <Button
              variant="outlined"
              onClick={resetSequence}
              size="small"
            >
              Start New Sequence
            </Button>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Error: {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailSequence;